import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Home.css';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import SharedHeader from './SharedHeader';
import { isQuickFlipSearchEnabled } from '../config/featureFlags';

function Home({ gradingOnly = false }) {
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();
  const { profitPlannerEnabled } = useFeatureFlags();
  const quickFlipEnabled = isQuickFlipSearchEnabled();
  
  // Move useState hooks to the top level
  const [faqItems] = useState([
    {
      id: 1,
      question: "What data does this tool use?",
      answer: "We pull the latest market data, including raw and graded card sales and grading trends, to calculate strong grading opportunities using a proprietary formula. Additionally, we apply a second proprietary method to measure each card's volatility and filter out misleading outliers to make sure you only see the most actionable insights."
    },
    {
      id: 2,
      question: "Is this for beginners or advanced investors?",
      answer: "Both! Whether you're just starting or flipping cards daily, COIN removes the guesswork and lets data drive your decisions. Even if you've never graded a card before, COIN helps you spot data-backed opportunities with strong grading upside in today's market in seconds. No deep market knowledge is required."
    },
    {
      id: 3,
      question: "How is COIN different from other card data tools?",
      answer: "Most card data tools focus on showing recent sales (comps) to help you understand market prices. That's valuable, but it still requires hours of manual research to identify grading upside.\n\nCOIN solves a different problem by analyzing and interpreting market data through the lens of a sports card investor. Instead of just displaying sales data and leaving you to figure out what it means, COIN does the heavy lifting for you by pulling all relevant insights into one place and ranking grading opportunities based on which cards are most likely to offer strong upside.\n\nMany serious investors use both - a market data tool for pricing comps and COIN for identifying high-upside grading plays in seconds."
    },
    {
      id: 4,
      question: "Will COIN tell me exactly which cards to buy?",
      answer: "COIN provides data-driven insights, but the final buying decision is up to you. It ranks grading opportunities based on up-to-date market data, but factors like card condition and your grading budget should still be considered. Think of COIN as your personal grading opportunity scanner - it finds strong plays based on the data, but you make the final call."
    },
    {
      id: 5,
      question: "Does this tool work for all sports and card types?",
      answer: "COIN is designed with a focus on modern sports cards and trading cards across baseball, basketball (including NBA and WNBA), football, hockey, and Pokemon. It focuses on the most frequently graded products from recent years where high-upside, clean ungraded cards can still be found before the market picks them over."
    },
    {
      id: 6,
      question: "How often is the data updated?",
      answer: "COIN updates multiple times daily, ensuring you always have the latest insights. The card market moves fast, and COIN keeps you ahead of the curve with scheduled automatic updates throughout the day so you don't fall behind."
    },
    {
      id: 7,
      question: "Are results guaranteed?",
      answer: "COIN identifies strong grading opportunities based on real-time market data, but no tool can predict the future. The insights provided reflect a snapshot in time, highlighting strong grading plays based on current sales trends, pricing, and grading data.\n\nWhile COIN removes the guesswork and helps you make more informed, data-driven decisions, profits are never guaranteed. Just like any investment, market conditions can change, and future values may differ from today's.\n\nGrading results also depend on submitting clean, high-quality cards, which is why careful card selection is key. Even the strongest grading opportunities won't pay off if the card isn't in clean condition."
    },
    {
      id: 8,
      question: "Will this work if I've never graded a card before?",
      answer: "Yes! COIN makes grading strategy easier than ever by showing you strong grading opportunities in an easy-to-understand format with no prior experience required.\n\nHowever, COIN is not a substitute for checking card condition. While it surfaces top opportunities based on market data, grading success still depends on submitting clean, high-quality cards. We strongly recommend carefully inspecting each card before submitting for grading, as condition directly impacts your results and grading outcome."
    },
    {
      id: 9,
      question: "Can I access COIN on my phone?",
      answer: "Yes! COIN is accessible on desktop, tablet, and mobile so you can analyze grading opportunities from anywhere, whether you're at home or at a card show."
    },
    {
      id: 10,
      question: "What happens after I sign up?",
      answer: "You'll get instant access to the COIN dashboard, where you can immediately start identifying high-upside grading opportunities and making data-driven decisions. There's no waiting - just instant insights."
    },
    {
      id: 11,
      question: "Do I need a subscription to keep using COIN?",
      answer: "Yes, COIN is a subscription-based tool that gives you continuous access to updated grading insights. The market moves fast, and staying ahead means having access to the latest data, which is why COIN updates multiple times per day."
    },
    {
      id: 12,
      question: "What if I don't want to commit long-term?",
      answer: "No worries! COIN is a month-to-month subscription with no contracts. You can cancel anytime—though once you see how much time and money it saves, you won't want to."
    },
    {
      id: 13,
      question: "How much does COIN cost?",
      answer: (
        <>
        COIN is $49.99/month. <br></br> <br></br>
        It's a premium tool that does what no other tool in the hobby does - it instantly turns market data into a ranked list of high-potential grading opportunities.<br></br> <br></br>
        There are no per-report fees and no upsells, just clear insights that help you grade with confidence and make more informed grading decisions. <br></br> <br></br>
        If you're not regularly grading cards or don't plan to be, COIN probably isn't a good fit.<br></br> <br></br>
        But if you actively buy modern cards to grade, some users report that a single submission using COIN pays for an entire year of access.
        </>
      )
    }
  ]);

  const [expandedItems, setExpandedItems] = useState({});

  // Toggle function for FAQ items
  const toggleFAQ = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // The public home page remains available to subscribers. The grading page,
  // however, should take authenticated users straight to the grading dashboard.
  useEffect(() => {
    if (gradingOnly && isAuthenticated && !loading) {
      navigate('/dashboard');
    }
  }, [gradingOnly, isAuthenticated, loading, navigate]);

  // Show loading state while checking auth
  if (loading) {
    return <div>Loading...</div>;
  }

  return (
      <div className="home-container">
        <SharedHeader presentationMode={isAuthenticated ? undefined : 'visitor'} />

        <main className="home-main">
          {!gradingOnly && <section className="home-pillars" aria-labelledby="home-pillars-title">
            <div className="home-pillars__intro">
              <h1 id="home-pillars-title">Built to help card investors take action on proven card market strategies.</h1>
            </div>

            <div className="home-pillar-grid">
              <article className="home-pillar-card home-pillar-card--grading" id="grading">
                <span className="home-pillar-card__number">01</span>
                <p className="home-pillar-card__label">Grading opportunity rankings</p>
                <h2>COIN Grading <span className="home-pillar-card__badge">Free trial</span></h2>
                <p>Find higher-upside cards to target for grading. Do 5 hours of research in 5 seconds.</p>
                <Link to="/grading">Learn More <span aria-hidden="true">→</span></Link>
              </article>

              <article className="home-pillar-card home-pillar-card--search" id="search">
                <span className="home-pillar-card__number">02</span>
                <p className="home-pillar-card__label">Advanced search builder + typo hunter</p>
                <h2>COIN Search <span className="home-pillar-card__badge">Free</span></h2>
                <p>Build advanced eBay searches in seconds. Find listings other buyers miss.</p>
                {quickFlipEnabled ? (
                  <Link to="/search">Open COIN Search <span aria-hidden="true">↗</span></Link>
                ) : (
                  <span className="home-pillar-card__unavailable">Coming soon</span>
                )}
              </article>

              {profitPlannerEnabled && <article className="home-pillar-card home-pillar-card--planner" id="profit-planner">
                <span className="home-pillar-card__number">03</span>
                <p className="home-pillar-card__label">Grading profit calculator</p>
                <h2>Profit Planner <span className="home-pillar-card__badge">Free</span></h2>
                <p>Set a monthly profit goal and see how many cards it takes, what a PSA 10 needs to sell for, and how much cash you need to start.</p>
                <Link to="/profit-planner">Open Profit Planner <span aria-hidden="true">→</span></Link>
              </article>}
            </div>

            {!isAuthenticated && (
              <p className="home-pillars__login">Already use COIN? <Link to="/login">Log In</Link></p>
            )}
          </section>}

          {gradingOnly && <>
          <section className="hero-section">
            <h2>
              <span>Want an Unfair Advantage in the Card Market?</span>
              <span>Instantly Identify High-Potential Raw Cards to Grade</span>
            </h2>
            
            <div className="hero-content">
              <div className="hero-text">
                <p>COIN (Patent Pending) is your data-driven edge in the card market.</p>
                <p>Using the latest market data, COIN identifies raw cards with strong grading upside and ranks them with our proprietary COIN Score formula - a powerful metric that analyzes multiple key data points to measure the grading upside of each card in today's market.</p>
                <p>That means you can stop guessing which cards to grade, stop spending hours on research, and stop wasting grading fees on cards with weak upside.</p>
                <p>COIN delivers a ranked list of high-potential raw cards to grade in today's market - backed by data, not opinions.</p>
                
                <button onClick={() => navigate('/register')} className="cta-button">
                  Start Free Trial
                </button>
              </div>
              
              <div className="hero-video">
                <iframe
                  src="https://player.vimeo.com/video/1064062806?h=fd501e9f28&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
                  title="COIN Tutorial Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </section>

          <section className="game-changer-section">
            <div className="game-changer-content">
              <div className="benefits-list">
                <blockquote className="testimonial">
                  "I was averaging about 4 hours per week researching to try to figure out what cards to grade. Now I know where to focus in seconds. This tool is a game-changer."
                  <cite>– Jake I.</cite>
                </blockquote>

                <button onClick={() => navigate('/register')} className="cta-button">
                  Start Free Trial
                </button>
              </div>
            </div>
          </section>

          <section className="home-faq-section">
            <h2>FAQs</h2>
            <div className="home-faq-content">
              <div className="home-faq-list">
                {faqItems.map((item) => (
                  <div key={item.id} className="home-faq-item">
                    <div 
                      className="home-faq-question"
                      onClick={() => toggleFAQ(item.id)}
                    >
                      <span className="home-faq-toggle">
                        {expandedItems[item.id] ? '−' : '+'}
                      </span>
                      <span className="home-question-text">❓ {item.question}</span>
                    </div>
                    {expandedItems[item.id] && (
                      <div className="home-faq-answer">
                      {typeof item.answer === 'string'
                    ? item.answer.split('\n\n').map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))
      : item.answer}
                      </div>
                    )}
                  </div>
                ))}

                <blockquote className="testimonial">
                  <p>💬 "I thought I'd try it for a month. It more than paid for itself in the first 10 minutes."</p>
                  <cite>– Paul S.</cite>
                </blockquote>

                <div className="final-cta">
                  <h2>Ready to get started?</h2>
                  <button onClick={() => navigate('/register')} className="cta-button">
                    Start Free Trial
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="instant-access-section">
            <h2>🚀 Get Instant Access to Strong Grading Opportunities - Backed by Real Data</h2>
            <div className="instant-access-content">
              <p>Every day, thousands of investors are grinding through hours of research. With COIN, you don't have to. You'll instantly see high-upside grading plays ranked with the latest data so you can make smarter, faster, and more informed decisions.</p>
              
              <blockquote className="testimonial">
                💬 "I graded two cards I wouldn't have even considered before using COIN. Both got PSA 10s and sold for 3X what I paid. If you're serious about grading, this is a no-brainer." - CJ B.
              </blockquote>

              <button onClick={() => navigate('/register')} className="cta-button">
                Start Free Trial
              </button>
            </div>
          </section>
          </>}
        </main>

        <footer className="home-footer" style={{ textAlign: 'center' }}>
          ©️{new Date().getFullYear()} Cardboard Edge LLC. All rights reserved.
          <br/>Cardboard Edge LLC
          <br/>5534 Saint Joe Road
          <br/>Fort Wayne, IN 46835
          <br/>
          <br/>Support:{' '}
          <a href="mailto:support@cardboardprofit.com">support@cardboardprofit.com</a>
        </footer>
      </div>
  );
}

export default Home;
